import React from "react";
import { theme } from "../../brand";
import { LOOK } from "../../b2c/look";

/**
 * Halloween reel, drawn art (flat SVG in the Frak look: no photo, no
 * emoji font). The night tones are the only colours outside `src/brand`
 * (dark violets); the orange / green come from the brand tokens.
 */
export const NIGHT = {
  deep: "#0b0922",
  violet: "#2b1d63",
  card: "#17133d",
  row: "#211b58",
  line: "#3d3189",
  /** Melted drips: lighter than the card so they read on the night. */
  drip: "#2f2580",
  bat: "#06051a",
} as const;

const ORANGE = theme.brand.colors.warning;
const GREEN = theme.brand.colors.success[800];

/** A friendly-spooky ghost. */
export const Ghost: React.FC<{ size: number; style?: React.CSSProperties }> = ({
  size,
  style,
}) => (
  <svg width={size} height={(size * 120) / 100} viewBox="0 0 100 120" style={style}>
    <path
      d="M8 112V50C8 18 28 4 50 4s42 14 42 46v62l-14-13-14 14-14-14-14 14-14-14z"
      fill={LOOK.white}
    />
    <path
      d="M8 112V86c14 8 30 2 42 10 12-8 28-2 42-10v26l-14-13-14 14-14-14-14 14-14-14z"
      fill={LOOK.sky}
    />
    <ellipse cx="35" cy="50" rx="8" ry="12" fill={NIGHT.deep} />
    <ellipse cx="65" cy="50" rx="8" ry="12" fill={NIGHT.deep} />
    <ellipse cx="50" cy="78" rx="9" ry="13" fill={NIGHT.deep} />
  </svg>
);

/** A jack-o'-lantern, lit from inside. */
export const Pumpkin: React.FC<{ size: number; style?: React.CSSProperties }> = ({
  size,
  style,
}) => (
  <svg width={size} height={(size * 120) / 140} viewBox="0 0 140 120" style={style}>
    <path d="M62 24c0-10 6-16 12-18l4 8c-6 2-8 6-8 12z" fill={GREEN} />
    <ellipse cx="70" cy="72" rx="64" ry="46" fill={ORANGE[800]} />
    <ellipse cx="42" cy="72" rx="38" ry="46" fill={ORANGE[700]} />
    <ellipse cx="98" cy="72" rx="38" ry="46" fill={ORANGE[700]} />
    <ellipse cx="70" cy="72" rx="32" ry="48" fill={ORANGE[600]} />
    <path d="M42 64l14 0-7-16zM84 64l14 0-7-16z" fill={ORANGE[200]} />
    <path
      d="M40 82l10 10 10-6 10 8 10-8 10 6 10-10-4 18H44z"
      fill={ORANGE[200]}
    />
  </svg>
);

/** A bat; `flap` 0..1 closes the wings. */
export const Bat: React.FC<{ size: number; flap: number }> = ({ size, flap }) => (
  <svg
    width={size}
    height={size / 2}
    viewBox="0 0 120 60"
    style={{ scale: `1 ${0.45 + 0.55 * flap}` }}
  >
    <path
      d="M54 28C38 6 14 10 2 28c12-2 18 4 24 12 6-6 14-4 20 4z"
      fill={NIGHT.bat}
    />
    <path
      d="M66 28c16-22 40-18 52 0-12-2-18 4-24 12-6-6-14-4-20 4z"
      fill={NIGHT.bat}
    />
    <ellipse cx="60" cy="34" rx="9" ry="15" fill={NIGHT.bat} />
    <path d="M54 22l2-9 5 6 5-6 2 9z" fill={NIGHT.bat} />
  </svg>
);

/** Four-point sparkle. */
export const Sparkle: React.FC<{ size: number; color: string }> = ({
  size,
  color,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path d="M12 0c1 8 4 11 12 12-8 1-11 4-12 12-1-8-4-11-12-12 8-1 11-4 12-12z" fill={color} />
  </svg>
);

/** The coin stack of the Frak widget's button. */
export const Coins: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill={LOOK.white}>
    <ellipse cx="18" cy="12" rx="12" ry="6" />
    <path d="M6 16c0 3.3 5.4 6 12 6s12-2.7 12-6v6c0 3.3-5.4 6-12 6S6 25.3 6 22z" />
    <ellipse cx="32" cy="28" rx="12" ry="6" />
    <path d="M20 32c0 3.3 5.4 6 12 6s12-2.7 12-6v6c0 3.3-5.4 6-12 6s-12-2.7-12-6z" />
  </svg>
);

const STAR = (cx: number, cy: number, big: number, small: number) =>
  Array.from({ length: 10 }, (_, i) => {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 ? small : big;
    return `${(cx + Math.cos(angle) * r).toFixed(1)},${(cy + Math.sin(angle) * r).toFixed(1)}`;
  }).join(" ");

/**
 * A magic wand, handle bottom-left, star tip top-right (200×200 box).
 * `star` scales the tip, `trail` (0..1) draws the light trail behind it.
 * Frak blue / white / gold.
 */
export const Wand: React.FC<{ size: number; star: number; trail: number }> = ({
  size,
  star,
  trail,
}) => {
  const gold = theme.brand.colors.warning[400];
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" style={{ overflow: "visible" }}>
      <path
        d="M150 50 Q92 28 38 84"
        fill="none"
        stroke={LOOK.white}
        strokeWidth={7}
        strokeLinecap="round"
        opacity={0.8 * trail}
      />
      <path
        d="M150 50 Q104 46 62 96"
        fill="none"
        stroke={gold}
        strokeWidth={4}
        strokeLinecap="round"
        opacity={0.9 * trail}
      />
      <line x1="30" y1="170" x2="142" y2="58" stroke={LOOK.white} strokeWidth={13} strokeLinecap="round" />
      <line x1="86" y1="114" x2="98" y2="102" stroke={LOOK.blue} strokeWidth={13} />
      <line x1="30" y1="170" x2="60" y2="140" stroke={gold} strokeWidth={17} strokeLinecap="round" />
      <circle cx="150" cy="50" r={38 * star} fill={gold} opacity={0.3} />
      <g transform={`translate(150 50) scale(${star}) translate(-150 -50)`}>
        <polygon
          points={STAR(150, 50, 28, 12)}
          fill={gold}
          stroke={LOOK.white}
          strokeWidth={4}
          strokeLinejoin="round"
        />
        <circle cx="150" cy="50" r="6" fill={LOOK.white} />
      </g>
    </svg>
  );
};
